'use client'

import { ReactNode, useCallback, useMemo, useState } from 'react'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  GenerateLabelModal,
  LabelType,
  DropOffPointType,
  getCollectionDates,
  transformCollectionDatesResponse,
  CollectionDatesModel,
  CollectionDateModel,
  LabelOptionsModel,
  transformLabelOptionsResponse,
  getLabelOptions,
  transformLabelOptionsRewriteResponse,
  isGetLabelOptionsRewriteResp,
  isGetLabelOptionsResp,
  LabelOptionsRewriteModel,
  LabelOptionsLabelType,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'

import GetShippingLabelContext, { GetShippingLabelContextType } from './GetShippingLabelContext'

type Props = {
  children: ReactNode
}

const GetShippingLabelContextProvider = ({ children }: Props) => {
  const translate = useTranslate('conversation.generic_error_modal')

  const [collectionDates, setCollectionDates] = useState<CollectionDatesModel | undefined>()
  const [selectedCollectionDate, setSelectedCollectionDate] = useState<
    CollectionDateModel | undefined
  >()
  const [availableCollectionDates, setAvailableCollectionDates] = useState<
    Array<CollectionDateModel> | undefined
  >()
  const [availableCollectionTimes, setAvailableCollectionTimes] = useState<
    Array<CollectionDateModel> | undefined
  >()
  const [selectedCollectionTime, setSelectedCollectionTime] = useState<
    CollectionDateModel | undefined
  >()
  const [confirmedCollectionDate, setConfirmedCollectionDate] = useState<
    CollectionDateModel | undefined
  >()
  const [confirmedCollectionTime, setConfirmedCollectionTime] = useState<
    CollectionDateModel | undefined
  >()
  const [uiState, setUiState] = useState(UiState.Idle)
  const [collectionDatesError, setCollectionDatesError] = useState<string | undefined>(undefined)
  const [selectedDropOffTypeKey, setSelectedDropOffTypeKey] = useState<number | string | null>(null)
  const [isDropOffTypeSubmitEnabled, setIsDropOffTypeSubmitEnabled] = useState(false)
  const [isLabelTypeSubmitEnabled, setIsLabelTypeSubmitEnabled] = useState(false)
  const [selectedLabelType, setSelectedLabelType] = useState<
    LabelType | LabelOptionsLabelType | null
  >(null)
  const [activeModal, setActiveModal] = useState<GenerateLabelModal>(GenerateLabelModal.None)
  const [labelOptionsError, setLabelOptionsError] = useState<string | undefined>(undefined)
  const [labelOptionsUiState, setLabelOptionsUiState] = useState(UiState.Idle)
  const [labelOptions, setLabelOptions] = useState<LabelOptionsModel | undefined>()
  const [labelOptionsRewrite, setLabelOptionsRewrite] = useState<
    LabelOptionsRewriteModel | undefined
  >()
  const [selectedDropOffPointType, setSelectedDropOffPointType] = useState<DropOffPointType | null>(
    null,
  )
  const [isDropOffPointTypeTypeSubmitEnabled, setIsDropOffPointTypeTypeSubmitEnabled] =
    useState(false)

  const fetchCollectionDates = useCallback(
    async ({ shipmentId }) => {
      setUiState(UiState.Pending)
      const response = await getCollectionDates(shipmentId)

      if ('errors' in response) {
        setCollectionDatesError(response.errors[0]?.value ?? translate('body'))
        setUiState(UiState.Failure)

        return
      }

      const transformedCollectionDates = transformCollectionDatesResponse(response)

      setCollectionDates(transformedCollectionDates)

      const uniqueDatesObj = transformedCollectionDates.dates.reduce(
        (uniqueDatesMap, { collection, collectionTo, dropOffTo }) => {
          const dateKey = collection?.split('T')[0]
          if (dateKey && !uniqueDatesMap[dateKey]) {
            return {
              ...uniqueDatesMap,
              [dateKey]: { collection, collectionTo, dropOffTo },
            }
          }

          return uniqueDatesMap
        },
        {} as Record<string, CollectionDateModel>,
      )

      const uniqueDates: Array<CollectionDateModel> = Object.values(uniqueDatesObj)

      const firstDate = transformedCollectionDates.dates[0]
      const firstDateCollection = firstDate?.collection?.split('T')[0]
      if (!firstDateCollection) return

      const sameDayCollectionTimes = transformedCollectionDates.dates.filter(date =>
        date.collection?.includes(firstDateCollection),
      )

      const firstSelectedTime = sameDayCollectionTimes[0]

      setAvailableCollectionDates(uniqueDates)
      setSelectedCollectionDate(firstDate)
      setAvailableCollectionTimes(sameDayCollectionTimes)
      setSelectedCollectionTime(firstSelectedTime)

      setUiState(UiState.Success)
    },
    [setCollectionDates, setUiState, setCollectionDatesError, translate],
  )

  const selectCollectionDate = useCallback(
    (collectionDate: CollectionDateModel) => {
      const dateString = collectionDate.collection?.split('T')[0]

      if (!dateString) return

      const filteredDates = collectionDates?.dates?.filter(date =>
        date.collection?.includes(dateString),
      )

      setSelectedCollectionDate(collectionDate)

      if (!filteredDates) return

      const firstAvailableTime = filteredDates[0]

      setAvailableCollectionTimes(filteredDates)
      setSelectedCollectionTime(firstAvailableTime)
    },
    [collectionDates],
  )

  const selectCollectionTime = useCallback((collectionTime: CollectionDateModel) => {
    setSelectedCollectionTime(collectionTime)
  }, [])

  const confirmCollectionDateSelection = useCallback(
    (collectionDate: CollectionDateModel, collectionTime: CollectionDateModel) => {
      setConfirmedCollectionDate(collectionDate)
      setConfirmedCollectionTime(collectionTime)
    },
    [],
  )

  const clearSelectedCollection = useCallback(() => {
    setCollectionDates(undefined)
    setSelectedCollectionDate(undefined)
    setSelectedCollectionTime(undefined)
    setAvailableCollectionDates(undefined)
    setAvailableCollectionTimes(undefined)
    setSelectedCollectionDate(undefined)
    setSelectedCollectionTime(undefined)
    setConfirmedCollectionTime(undefined)
    setConfirmedCollectionDate(undefined)
  }, [])

  const clearCollectionDatesError = useCallback(() => {
    setCollectionDatesError(undefined)
  }, [])

  const selectDropOffTypeKey = useCallback((dropOffTypeKey: number | string) => {
    setSelectedDropOffTypeKey(dropOffTypeKey)
    setIsDropOffTypeSubmitEnabled(true)
  }, [])

  const handleClearDropOffTypeSelection = useCallback(() => {
    setSelectedDropOffTypeKey(null)
    setIsDropOffTypeSubmitEnabled(false)
  }, [])

  const selectLabelType = useCallback((selectedType: LabelType | LabelOptionsLabelType) => {
    setSelectedLabelType(selectedType)
    setIsLabelTypeSubmitEnabled(true)
  }, [])

  const clearLabelTypeSelection = useCallback(() => {
    setSelectedLabelType(null)
    setIsLabelTypeSubmitEnabled(false)
  }, [])

  const handleSetActiveModal = useCallback((modal: GenerateLabelModal) => {
    setActiveModal(modal)
  }, [])

  const fetchLabelOptions = useCallback(
    async args => {
      setLabelOptionsUiState(UiState.Pending)
      const response = await getLabelOptions(args.shipmentId)

      if ('errors' in response) {
        setLabelOptionsError(response.errors[0]?.value)
        setLabelOptionsUiState(UiState.Failure)

        return
      }

      if (isGetLabelOptionsRewriteResp(response)) {
        const transformedLabelOptions = transformLabelOptionsRewriteResponse(response)

        setLabelOptionsRewrite(transformedLabelOptions)
      }
      if (isGetLabelOptionsResp(response)) {
        const transformedLabelOptions = transformLabelOptionsResponse(response)

        setLabelOptions(transformedLabelOptions)
      }

      setLabelOptionsUiState(UiState.Success)
    },
    [setLabelOptions, setLabelOptionsUiState, setLabelOptionsError],
  )

  const handleClearLabelOption = useCallback(() => {
    setLabelOptions(undefined)
  }, [])

  const selectDropOffPointType = useCallback((selectedDropOffType: DropOffPointType) => {
    setSelectedDropOffPointType(selectedDropOffType)
    setIsDropOffPointTypeTypeSubmitEnabled(true)
  }, [])

  const handleClearDropOffPointTypeSelection = useCallback(() => {
    setSelectedDropOffPointType(null)
    setIsDropOffPointTypeTypeSubmitEnabled(false)
  }, [])

  const contextValues = useMemo<GetShippingLabelContextType>(
    () => ({
      state: {
        collectionDates: {
          fetchCollectionDates,
          uiState,
          collectionDates,
          availableCollectionDates,
          selectedCollectionDate,
          availableCollectionTimes: availableCollectionTimes || undefined,
          selectedCollectionTime,
          collectionDatesError,
          confirmedCollectionDate,
          confirmedCollectionTime,
          selectCollectionDate,
          selectCollectionTime,
          clearSelectedCollection,
          confirmCollectionDateSelection,
          clearCollectionDatesError,
        },
        dropOffTypeSelection: {
          selectedDropOffTypeKey,
          isSubmitEnabled: isDropOffTypeSubmitEnabled,
          clearDropOffTypeSelection: handleClearDropOffTypeSelection,
          selectDropOffTypeKey,
        },
        labelTypeSelection: {
          isLabelTypeSubmitEnabled,
          selectLabelType,
          clearLabelTypeSelection,
          selectedLabelType,
        },
        activeModal: {
          activeModal,
          setActiveModal: handleSetActiveModal,
        },
        labelOptions: {
          fetchLabelOptions,
          labelOptions,
          clearLabelOption: handleClearLabelOption,
          labelOptionsError,
          labelOptionsUiState,
          labelOptionsRewrite,
        },
        dropOffPointTypeSelection: {
          selectedDropOffPointType,
          selectDropOffPointType,
          handleClearDropOffPointTypeSelection,
          isDropOffPointTypeTypeSubmitEnabled,
        },
      },
    }),
    [
      fetchCollectionDates,
      uiState,
      collectionDates,
      availableCollectionDates,
      selectedCollectionDate,
      availableCollectionTimes,
      selectedCollectionTime,
      collectionDatesError,
      confirmedCollectionDate,
      confirmedCollectionTime,
      selectCollectionDate,
      selectCollectionTime,
      clearSelectedCollection,
      confirmCollectionDateSelection,
      selectedDropOffTypeKey,
      isDropOffTypeSubmitEnabled,
      handleClearDropOffTypeSelection,
      selectDropOffTypeKey,
      isLabelTypeSubmitEnabled,
      selectLabelType,
      clearLabelTypeSelection,
      selectedLabelType,
      activeModal,
      handleSetActiveModal,
      fetchLabelOptions,
      labelOptions,
      handleClearLabelOption,
      labelOptionsError,
      labelOptionsUiState,
      clearCollectionDatesError,
      selectedDropOffPointType,
      selectDropOffPointType,
      handleClearDropOffPointTypeSelection,
      isDropOffPointTypeTypeSubmitEnabled,
      labelOptionsRewrite,
    ],
  )

  return (
    <GetShippingLabelContext.Provider value={contextValues}>
      {children}
    </GetShippingLabelContext.Provider>
  )
}

export default GetShippingLabelContextProvider
